import { Button, Input } from "@aix/ui"
import { ReactTable } from "@aix/ui"
import { BookOpen, Edit2, GripVertical, Plus, Trash2, X } from "lucide-react"
import { useMemo, useState } from "react"
import toast from "react-hot-toast"
import { trpc } from "../../lib/trpc"

export default function AdminJourneysScreen() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingJourney, setEditingJourney] = useState<any>(null)

  const { data: journeys, isLoading, refetch } = trpc.videoOnboarding.listJourneys.useQuery({})
  const { data: allVideos } = trpc.videoOnboarding.listVideos.useQuery({ limit: 100 })

  const createMutation = trpc.videoOnboarding.createJourney.useMutation({
    onSuccess: () => {
      toast.success("Journey created successfully!")
      setIsCreateModalOpen(false)
      void refetch()
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create journey")
    },
  })

  const updateMutation = trpc.videoOnboarding.updateJourney.useMutation({
    onSuccess: () => {
      toast.success("Journey updated successfully!")
      setEditingJourney(null)
      void refetch()
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update journey")
    },
  })

  const deleteMutation = trpc.videoOnboarding.deleteJourney.useMutation({
    onSuccess: () => {
      toast.success("Journey deleted successfully!")
      void refetch()
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete journey")
    },
  })

  const columns = useMemo(
    () => [
      {
        Header: "Journey",
        accessor: "name",
        Cell: ({ row }: any) => (
          <div className="flex items-center gap-3">
            <span className="text-2xl">{row.icon || "📚"}</span>
            <div>
              <div className="font-semibold text-primary">{row.name}</div>
              {row.description && <div className="text-sm text-muted-foreground line-clamp-1">{row.description}</div>}
            </div>
          </div>
        ),
      },
      {
        Header: "Videos",
        accessor: "videoCount",
        Cell: ({ row }: any) => <span className="text-muted-foreground">{row.videoCount || 0}</span>,
      },
      {
        Header: "Duration",
        accessor: "estimatedMinutes",
        Cell: ({ row }: any) => (row.estimatedMinutes ? `${row.estimatedMinutes} min` : "-"),
      },
      {
        Header: "Tags",
        accessor: "tags",
        Cell: ({ row }: any) => (
          <div className="flex flex-wrap gap-1">
            {row.tags?.map((tag: string) => (
              <span key={tag} className="px-2 py-1 bg-secondary rounded text-xs">
                {tag}
              </span>
            ))}
          </div>
        ),
      },
      {
        Header: "Actions",
        accessor: "id",
        Cell: ({ row }: any) => (
          <div className="flex gap-2">
            <button onClick={() => setEditingJourney(row)} className="p-2 hover:bg-accent rounded transition-colors">
              <Edit2 className="w-4 h-4 text-muted-foreground" />
            </button>
            <button
              onClick={() => {
                if (confirm(`Delete "${row.name}"?`)) {
                  deleteMutation.mutate({ journeyId: row.id })
                }
              }}
              className="p-2 hover:bg-destructive/10 rounded transition-colors"
            >
              <Trash2 className="w-4 h-4 text-destructive" />
            </button>
          </div>
        ),
      },
    ],
    [deleteMutation],
  )

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-primary mb-2">Manage Journeys</h1>
            <p className="text-muted-foreground">Create and organize learning paths</p>
          </div>
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Journey
          </Button>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : journeys && journeys.length > 0 ? (
          <ReactTable data={journeys} columns={columns} />
        ) : (
          <div className="text-center py-12 bg-secondary rounded-lg">
            <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No journeys created yet</p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {isCreateModalOpen && (
        <JourneyModal
          videos={allVideos || []}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          isLoading={createMutation.isLoading}
        />
      )}

      {/* Edit Modal */}
      {editingJourney && (
        <JourneyModal
          journey={editingJourney}
          videos={allVideos || []}
          onClose={() => setEditingJourney(null)}
          onSubmit={(data) => updateMutation.mutate({ journeyId: editingJourney.id, ...data })}
          isLoading={updateMutation.isLoading}
        />
      )}
    </div>
  )
}

// Journey Modal Component (Create & Edit)
function JourneyModal({
  journey,
  videos,
  onClose,
  onSubmit,
  isLoading,
}: {
  journey?: any
  videos: any[]
  onClose: () => void
  onSubmit: (data: any) => void
  isLoading: boolean
}) {
  const [name, setName] = useState(journey?.name || "")
  const [description, setDescription] = useState(journey?.description || "")
  const [icon, setIcon] = useState(journey?.icon || "📚")
  const [tags, setTags] = useState(journey?.tags?.join(", ") || "")
  const [selectedVideoIds, setSelectedVideoIds] = useState<string[]>(journey?.videoIds || [])
  const [searchQuery, setSearchQuery] = useState("")

  const filteredVideos = useMemo(() => {
    if (!searchQuery) return videos
    const query = searchQuery.toLowerCase()
    return videos.filter(
      (v) => v.title.toLowerCase().includes(query) || v.tags?.some((t: string) => t.toLowerCase().includes(query)),
    )
  }, [videos, searchQuery])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      name,
      description,
      icon,
      tags: tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      videoIds: selectedVideoIds,
    })
  }

  const toggleVideo = (videoId: string) => {
    setSelectedVideoIds((prev) => (prev.includes(videoId) ? prev.filter((id) => id !== videoId) : [...prev, videoId]))
  }

  const moveVideo = (fromIndex: number, toIndex: number) => {
    const newOrder = [...selectedVideoIds]
    const [movedId] = newOrder.splice(fromIndex, 1)
    newOrder.splice(toIndex, 0, movedId)
    setSelectedVideoIds(newOrder)
  }

  const selectedVideos = selectedVideoIds.map((id) => videos.find((v) => v.id === id)).filter(Boolean)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-background rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-2xl font-bold text-primary mb-4">{journey ? "Edit" : "Create"} Journey</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Name *</label>
              <Input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Icon</label>
              <Input type="text" value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="📚" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              rows={2}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Tags (comma-separated)</label>
            <Input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="beginner, engineering, onboarding"
            />
          </div>

          {/* Video Selection */}
          <div className="border-t border-border pt-4">
            <h3 className="text-lg font-semibold text-primary mb-3">Videos ({selectedVideoIds.length})</h3>

            {/* Selected Videos (Drag & Drop) */}
            {selectedVideos.length > 0 && (
              <div className="mb-4 space-y-2">
                <p className="text-sm text-muted-foreground mb-2">Selected videos (drag to reorder):</p>
                {selectedVideos.map((video: any, index: number) => (
                  <div key={video.id} className="flex items-center gap-3 bg-secondary p-3 rounded border border-border">
                    <button
                      type="button"
                      className="cursor-move text-muted-foreground"
                      onMouseDown={(e) => {
                        // Simple drag implementation
                        const startY = e.clientY
                        const startIndex = index

                        const handleMove = (e: MouseEvent) => {
                          const deltaY = e.clientY - startY
                          const newIndex = Math.max(
                            0,
                            Math.min(selectedVideos.length - 1, startIndex + Math.round(deltaY / 60)),
                          )
                          if (newIndex !== startIndex) {
                            moveVideo(startIndex, newIndex)
                          }
                        }

                        const handleUp = () => {
                          document.removeEventListener("mousemove", handleMove)
                          document.removeEventListener("mouseup", handleUp)
                        }

                        document.addEventListener("mousemove", handleMove)
                        document.addEventListener("mouseup", handleUp)
                      }}
                    >
                      <GripVertical className="w-5 h-5" />
                    </button>
                    <span className="text-sm font-semibold text-muted-foreground min-w-[20px]">{index + 1}</span>
                    <span className="flex-1 text-sm">{video.title}</span>
                    <button
                      type="button"
                      onClick={() => toggleVideo(video.id)}
                      className="p-1 hover:bg-destructive/10 rounded transition-colors"
                    >
                      <X className="w-4 h-4 text-destructive" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Available Videos */}
            <div>
              <p className="text-sm text-muted-foreground mb-2">Add videos:</p>
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search videos..."
                className="mb-3"
              />
              <div className="max-h-64 overflow-y-auto space-y-2 bg-secondary/50 p-3 rounded">
                {filteredVideos.map((video: any) => (
                  <label
                    key={video.id}
                    className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                      selectedVideoIds.includes(video.id)
                        ? "bg-primary/10 border border-primary"
                        : "hover:bg-background"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedVideoIds.includes(video.id)}
                      onChange={() => toggleVideo(video.id)}
                      className="w-4 h-4"
                    />
                    <span className="flex-1 text-sm">{video.title}</span>
                    {video.duration && (
                      <span className="text-xs text-muted-foreground">{Math.ceil(video.duration / 60)} min</span>
                    )}
                  </label>
                ))}
                {filteredVideos.length === 0 && (
                  <p className="text-center text-muted-foreground py-4">No videos found</p>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" loading={isLoading} className="flex-1">
              {journey ? "Save" : "Create"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
